/**
 * Save/restore the Home window scroll position across navigation.
 *
 * Home scrolls the document body (no inner scroll container), and results load
 * asynchronously, so the browser's native scroll restoration can't be relied
 * on. We stash the scroll offset in sessionStorage keyed by the search (see
 * `homeSearchKey`) just before leaving Home, and restore it once the matching
 * results have rendered on return. Keyed by search so a different search after
 * Back doesn't jump to a stale offset.
 */

const KEY_PREFIX = 'homeScroll:';

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export function saveHomeScroll(searchKey: string): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(`${KEY_PREFIX}${searchKey}`, String(window.scrollY));
  } catch {
    // sessionStorage can throw (private mode / quota) — scroll restore is a
    // nice-to-have, so fail silently.
  }
}

/** Returns the saved scroll offset for the search, or null if none stored. */
export function readHomeScroll(searchKey: string): number | null {
  const store = storage();
  if (!store) return null;
  const raw = store.getItem(`${KEY_PREFIX}${searchKey}`);
  if (raw === null) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function clearHomeScroll(searchKey: string): void {
  const store = storage();
  if (!store) return;
  store.removeItem(`${KEY_PREFIX}${searchKey}`);
}
